import React from 'react';
import * as Sentry from '@sentry/react';
import { ApiError } from '@/api/ApiError';
import { ErrorCodes } from 'schemas';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
}

// Default fallback renderer to access error, componentStack, eventId
const DefaultErrorFallback: React.FC<{
  error: unknown;
  eventId: string;
  componentStack: string;
  resetError: () => void;
}> = ({ eventId }) => {
  // Convert to ApiError for consistent error handling
  const apiError = new ApiError({
    type: 'error',
    error: 'Something went wrong',
    code: ErrorCodes.INTERNAL_SERVER_ERROR,
    message: 'An unexpected error occurred. Please try refreshing the page.',
  });

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>
            {apiError.message}
            {eventId ? (
              <div className="mt-2 text-xs opacity-70">Error ID: {eventId}</div>
            ) : null}
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({
  children,
}) => {
  return (
    <Sentry.ErrorBoundary
      fallback={(props: {
        error: unknown;
        eventId: string;
        componentStack: string;
        resetError: () => void;
      }) => <DefaultErrorFallback {...props} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
